import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Pricing-redesign: CarrierRateBand admin client.
 *
 * Stores self-rated label pricing for OWNED carriers (e.g. EZ Fleet). Each row
 * is keyed on (carrierService × fromArea × toArea × weight bucket × validFrom).
 * External carriers don't use this — their rates come from the carrier API.
 */

export interface CarrierRateBand {
  id: string;
  carrierServiceId: string;
  carrierService?: {
    id: string;
    code: string;
    name: string;
    carrier: { id: string; code: string; name: string };
  };
  fromServiceAreaId: string;
  fromServiceArea?: { id: string; code: string; name: string };
  toServiceAreaId: string;
  toServiceArea?: { id: string; code: string; name: string };
  weightFromKg: number; // inclusive
  weightToKg: number; // exclusive
  baseRateCents: number;
  // Pricing-merger: smooth-pricing slope. NULL → flat band.
  perKgCents: number | null;
  anchorWeightKg: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode: string; // "CAD"
  validFrom: string; // ISO date
  validTo?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCarrierRateBandInput {
  carrierServiceId: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
  validFrom: string;
  validTo?: string | null;
  active?: boolean;
}

export type UpdateCarrierRateBandInput = Partial<CreateCarrierRateBandInput>;

export function useGetCarrierRateBands(params?: {
  carrierServiceId?: string;
  fromServiceAreaId?: string;
  toServiceAreaId?: string;
  active?: boolean;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-rate-bands", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.carrierServiceId)
        q.append("carrierServiceId", params.carrierServiceId);
      if (params?.fromServiceAreaId)
        q.append("fromServiceAreaId", params.fromServiceAreaId);
      if (params?.toServiceAreaId)
        q.append("toServiceAreaId", params.toServiceAreaId);
      if (params?.active !== undefined)
        q.append("active", String(params.active));

      const { data } = await axios.get<{
        success: boolean;
        data: CarrierRateBand[];
      }>(
        `${API_URL}/api/v2/admin/carrier-rate-bands${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreateCarrierRateBand(options?: {
  onSuccess?: (data: CarrierRateBand) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCarrierRateBandInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: CarrierRateBand;
      }>(`${API_URL}/api/v2/admin/carrier-rate-bands`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-rate-bands"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateCarrierRateBand(options?: {
  onSuccess?: (data: CarrierRateBand) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateCarrierRateBandInput;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: CarrierRateBand;
      }>(`${API_URL}/api/v2/admin/carrier-rate-bands/${id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-rate-bands"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteCarrierRateBand(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/admin/carrier-rate-bands/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-rate-bands"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Pricing-merger Phase 4: atomic-save mutation for the matrix editor.
 *
 * Replaces ALL bands for a single (carrierServiceId, validFrom) slot in one
 * transaction. Server validates per-lane continuity and rejects on
 * BAND_CONTINUITY_VIOLATION unless `intentionalDiscontinuity: true` passed.
 */
export interface AtomicSaveBandInput {
  fromServiceAreaId: string;
  toServiceAreaId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
  validTo?: string | null;
  active?: boolean;
}

/**
 * One stored row as the client last read it.
 *
 * The atomic save replaces the whole slice, so without this a second editor's
 * save silently reverts the first — no error, nothing on screen. The Server
 * compares this against what it holds and refuses with 409 if it has moved.
 * `updatedAt` matters as much as `id`: a per-row edit keeps every id.
 */
export interface RateBandBasisRow {
  id: string;
  updatedAt: string;
}

export interface AtomicSaveCarrierRateBandsInput {
  carrierServiceId: string;
  validFrom: string;
  bands: AtomicSaveBandInput[];
  /** Required — the rows this payload was built from. */
  basis: RateBandBasisRow[];
  intentionalDiscontinuity?: boolean;
}

export interface ContinuityViolation {
  upperBandWeightFromKg: number;
  expectedBaseRateCents: number;
  actualBaseRateCents: number;
  diffCents: number;
}

export class BandContinuityViolationError extends Error {
  constructor(
    public violations: ContinuityViolation[],
    message: string
  ) {
    super(message);
    this.name = "BandContinuityViolationError";
  }
}

export function useAtomicSaveCarrierRateBands(options?: {
  onSuccess?: (data: CarrierRateBand[]) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: AtomicSaveCarrierRateBandsInput
    ): Promise<CarrierRateBand[]> => {
      try {
        const { data } = await axios.post<{
          success: boolean;
          data: CarrierRateBand[];
          meta: { laneGroups: number; bandsCreated: number };
        }>(`${API_URL}/api/v2/admin/carrier-rate-bands/atomic-save`, input, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return data.data;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 400) {
          const body = err.response.data as {
            error?: string;
            details?: { message?: string; violations?: ContinuityViolation[] };
          };
          if (body?.error === "BAND_CONTINUITY_VIOLATION") {
            throw new BandContinuityViolationError(
              body.details?.violations ?? [],
              body.details?.message ?? "Band continuity violation"
            );
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-rate-bands"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function formatWeightKg(kg: number | string): string {
  const n = typeof kg === "string" ? parseFloat(kg) : kg;
  return `${n.toFixed(2)} kg`;
}
